// src/encounter/lib/signed-refusal.ts
// PROD-2810 / rule 38: a write to a PROVIDER-signed encounter is refused by the server with 409 and
// the code `encounter-signed` (QuestionBank, Scrips.Patient `SignedEncounterWrite`, ambient: the same
// word). Dev enforces it (`SignedNoteFreeze:Enforce`). A refused save must never be silent: the page
// says so in these words and does not show the step as saved or completed.
//
// ONE source for the sentence (design desk, 6 Oct). There is no amendment route any more (Flutter sign
// model, 14 Oct 2026), so a refused note answer and a refused encounter-record field (vitals, chief
// complaint, Fall / Pain) say the same plain sentence.
import { ApiError } from '@/shared/api/api-client';

export const SIGNED_NOT_SAVED = 'Not saved — this note is signed.';

/** Frame v2's header save line for the sentences above (audit FV2-31): the banner above the
 *  canvas carries the sentence once; the header says only this. */
export const NOT_SAVED = 'Not saved';

/** Which Fall / Pain list, in words. */
const riskListWords = ({ fall, pain }: { fall: boolean; pain: boolean }) =>
  fall && pain ? 'the fall and pain assessments' : fall ? 'the fall assessment' : 'the pain assessment';

/**
 * PROD-3142: a Fall / Pain save refused as stale (412) — someone (another window, or a colleague) changed the stored list
 * since this visit read it. Nothing was overwritten; the clinician reloads to see the newer list. Words: design desk D-78.
 * Null when nothing was refused.
 */
export function riskChangedElsewhereSentence(conflict: { fall: boolean; pain: boolean }): string | null {
  if (!conflict.fall && !conflict.pain) return null;
  return `Not saved — someone changed ${riskListWords(conflict)} after this visit was opened. Reload the encounter to see the latest, then enter your change again.`;
}

/** PROD-3142 (D-78): why Sign is disabled while such a refusal stands — a signature would attest a list this clinician
 *  has not seen. Null when nothing was refused. */
export function riskChangedSignReason(conflict: { fall: boolean; pain: boolean }): string | null {
  if (!conflict.fall && !conflict.pain) return null;
  return `Reload the encounter first: ${riskListWords(conflict)} changed elsewhere.`;
}

/** True when `text` is one of the signed-refusal sentences, or the Fall / Pain changed-elsewhere sentence (the banner's
 *  words; the frame v2 header then says only "Not saved"). */
export function isSignedRefusalSentence(text: string): boolean {
  return (
    text === SIGNED_NOT_SAVED ||
    /^Not saved — someone changed the (?:fall|pain|fall and pain) assessments? after this visit was opened\./.test(text)
  );
}

export interface SignedRefusals {
  /** A note answer (QuestionnaireResponse) save was refused. */
  answers: boolean;
  /** An encounter-record save (vitals, chief complaint, Fall / Pain) was refused. */
  records: boolean;
}

/** The sentence for what was refused, or null when nothing was. */
export function signedRefusalSentence({ answers, records }: SignedRefusals): string | null {
  return answers || records ? SIGNED_NOT_SAVED : null;
}

/** PROD-3142: the server refused a versioned Fall / Pain save because the stored list changed since it was read (412
 *  `stale-version`, Scrips.Patient #1051). Nothing was written. */
export function isStaleRiskSave(error: unknown): boolean {
  return error instanceof ApiError && error.status === 412;
}

/**
 * True when `error` is the server refusing a write because the encounter is signed: a 409 whose body
 * carries `code: "encounter-signed"`. A 409 with any other code is something else (a conflict the
 * caller handles), and so is every other status.
 */
export function isSignedNoteRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  try {
    const body = JSON.parse(error.body) as { code?: unknown } | null;
    return body?.code === 'encounter-signed';
  } catch {
    return false;
  }
}
